/**
 * Port 抽象。對應 docs/wire.md §1。
 *
 * 整條鏈路寫在 Port 之上，因此可以在 Node 裡用一對假 Port 測完，
 * 完全不需要瀏覽器。真正的 iframe 綁定只是換一個 Port 實作。
 */

export interface Port {
  post(data: unknown): void
  onMessage(handler: (data: unknown) => void): void
  onClose(handler: () => void): void
  close(): void
}

/** MessagePort 的結構型別。刻意不引用 DOM 型別——kernel 不得依賴 DOM。 */
export interface MessagePortLike {
  postMessage(data: unknown): void
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void
  start?: () => void
  close(): void
}

class BasePort implements Port {
  protected messageHandlers: ((data: unknown) => void)[] = []
  protected closeHandlers: (() => void)[] = []
  protected closed = false

  post(_data: unknown): void {
    throw new Error('not implemented')
  }

  onMessage(handler: (data: unknown) => void): void {
    this.messageHandlers.push(handler)
  }

  onClose(handler: () => void): void {
    if (this.closed) {
      queueMicrotask(handler)
      return
    }
    this.closeHandlers.push(handler)
  }

  close(): void {
    if (this.closed) return
    this.closed = true
    for (const handler of this.closeHandlers) handler()
    this.closeHandlers = []
  }

  protected receive(data: unknown): void {
    if (this.closed) return
    for (const handler of this.messageHandlers) handler(data)
  }
}

class MemoryPort extends BasePort {
  peer: MemoryPort | null = null

  override post(data: unknown): void {
    if (this.closed) return
    const peer = this.peer
    if (!peer) return
    // 模擬跨邊界：一律非同步，且序列化一次（真正的 postMessage 會複製）
    const copy = JSON.parse(JSON.stringify(data)) as unknown
    queueMicrotask(() => peer.receive(copy))
  }

  override close(): void {
    if (this.closed) return
    const peer = this.peer
    this.peer = null
    super.close()
    // 已經送出的訊息必須先送達，關閉才生效——真正的 postMessage 就是這個順序。
    // 例如 refuse 之後立刻關閉，對方仍應先收到 refuse 才收到斷線。
    if (peer) {
      queueMicrotask(() => {
        peer.peer = null
        peer.close()
      })
    }
  }
}

/** 測試用：兩個互相對接的記憶體 Port。關閉任一端會連帶關閉另一端。 */
export function createPortPair(): [Port, Port] {
  const a = new MemoryPort()
  const b = new MemoryPort()
  a.peer = b
  b.peer = a
  return [a, b]
}

/** 正式環境：把一個 MessagePort 包成 Port */
export function messagePortAdapter(mp: MessagePortLike): Port {
  const port = new (class extends BasePort {
    override post(data: unknown): void {
      if (this.closed) return
      mp.postMessage(data)
    }

    override close(): void {
      if (this.closed) return
      super.close()
      mp.close()
    }

    deliver(data: unknown): void {
      this.receive(data)
    }
  })()

  mp.addEventListener('message', (event) => {
    ;(port as unknown as { deliver(data: unknown): void }).deliver(event.data)
  })
  mp.start?.()

  return port
}
